import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Spinner from 'common/spinner/components/spinner';
import { Button, Icon, ScrollBar } from 'ui';

const Card = styled.div`
  width: inherit;
  height: inherit;
  border-radius: 10px;
  background-color: ${p => p.backgroundColor};
  box-shadow: 5px 5px 5px #A9BEC8;
`;

const CardHeader = styled.div`
  background-color: ${p => p.backgroundColor};
  ${p => p.headerBackgroundImage && `
    background-image: ${p.headerBackgroundImage};
    background-repeat: no-repeat;
  `}
  color: ${p => p.headerTextColor};
  border-radius: 10px 10px 0 0;
  padding: 3px 10px 0 10px;
  height: auto;
  font-size: 20px;
  font-weight: bold;
  position: relative;
  border-bottom: 1px solid #D9E0E6;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const DragIcon = styled.div`
  color: ${p => p.theme.colors.theme} !important;
  position: absolute;
  right: 10px;
  top: 12px;
  font-size: 14px;
  cursor: move;
  i {
    color: ${p => p.iconColor || p.theme.colors.theme} !important;
    cursor: move;
  }
`;

const CardContent = styled.div`
  margin-top: 10px;
  padding: 0 10px 10px 10px;
  height: ${p => p.height};
  ${p => p.canScrollY ? 'overflow-y: auto;' : ''}
  margin-right: 5px;
  ${ScrollBar}
`;

const CardAction = styled.div`
  margin: 0 3px 10px 0;
  ${p => p.actionInToolBar ? `
    position: fixed;
    right: 20px;
    top: 45px;
  ` :
    ' float: right;'
  }
`;

const CardDescription = styled.div`
  display: block;
  height: 100%;
  margin-right: 5px;
  ${p => p.actionInToolBar ? `
    margin-top: 50px;
    height: calc(100% - 44px);
    ${!p.canScrollY ? 'overflow-y: auto;' : ''}
    ${ScrollBar}
  ` : ''}
`;

const Title = styled.p`
  width: fit-content;
  max-width: calc(100% - 20px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  padding: 5px 0;
  border-bottom: 2px solid ${p => p.iconColor || p.theme.colors.theme};
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;
  color: white;
  background-color: ${p => p.theme.colors.theme};
  font-size: 14px;
  height: 30px;
  line-height: 0px;
  &:hover {
    color: white;
    background-color: ${p => p.theme.colors.theme};
    opacity: 0.7;
  }
`;

const CardTile = ({
  title,
  description,
  backgroundColor = 'white',
  headerTextColor = 'black',
  headerBackgroundColor = 'white',
  action,
  isDraggable,
  actionInToolBar = false,
  canScrollY = true,
  headerIconColor,
  headerBackgroundImage,
  renderHeaderIcon,
  isLoading,
  ...rest
}) => {
  const handleAction = () => {
    if (action?.type === 'redirect') {
      window.location = action.url;
    }
  };
  return (
    <Card backgroundColor={backgroundColor} {...rest}>
      <CardHeader
        backgroundColor={headerBackgroundColor}
        headerTextColor={headerTextColor}
        headerBackgroundImage={headerBackgroundImage}
      >
        <Title iconColor={headerIconColor}>
          {renderHeaderIcon && renderHeaderIcon()}
          {title}
        </Title>
        {isDraggable &&
          <DragIcon className='draggable_handle' iconColor={headerIconColor}>
            <Icon type='arrows-alt' />
          </DragIcon>
        }
      </CardHeader>
      {isLoading ?
        <Spinner /> :
        <CardContent height={'calc(100% - 65px)'} canScrollY={canScrollY}>
          {action && <CardAction actionInToolBar={actionInToolBar}>
            <StyledButton onClick={handleAction}>{action.buttonText}</StyledButton>
          </CardAction>}
          <CardDescription actionInToolBar={actionInToolBar} canScrollY={canScrollY}>
            {description}
          </CardDescription>
        </CardContent>
      }
    </Card>
  );
};

CardTile.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  backgroundColor: PropTypes.string,
  headerBackgroundColor: PropTypes.string,
  width: PropTypes.number,
  action: PropTypes.string,
  isDraggable: PropTypes.bool,
  actionInToolBar: PropTypes.bool,
  canScrollY: PropTypes.bool,
  headerTextColor: PropTypes.string,
  headerIconColor: PropTypes.string,
  headerBackgroundImage: PropTypes.string,
  renderHeaderIcon: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default CardTile;
